import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useStoreSettings } from "@/hooks/useStoreSettings";
import { NavLink } from "@/components/NavLink";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Truck,
  CalendarClock,
  BarChart3,
  Users,
  LogOut,
  Store,
  Tags,
  Building2,
  Settings,
  ChevronLeft,
  ExternalLink,
  ImageIcon,
  MessageSquare,
  Ticket,
  DollarSign,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import codxisIcon from "@/assets/codxis-icon-white.png";

const mainNav = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/orders", icon: ShoppingCart, label: "Pedidos" },
  { to: "/admin/reviews", icon: MessageSquare, label: "Avaliações" },
  { to: "/admin/banners", icon: ImageIcon, label: "Banners" },
  { to: "/admin/coupons", icon: Ticket, label: "Cupons" },
];

const catalogNav = [
  { to: "/admin/products", icon: Package, label: "Produtos" },
  { to: "/admin/categories", icon: Tags, label: "Categorias" },
  { to: "/admin/brands", icon: Building2, label: "Marcas" },
  { to: "/admin/stock-reports", icon: BarChart3, label: "Relatórios Estoque" },
];

const financialNav = [
  { to: "/admin/financial", icon: DollarSign, label: "Financeiro" },
];

const operationsNav = [
  { to: "/admin/delivery", icon: Truck, label: "Entregas" },
  { to: "/admin/schedules", icon: CalendarClock, label: "Agendamentos" },
];

const systemNav = [
  { to: "/admin/users", icon: Users, label: "Usuários" },
  { to: "/admin/settings", icon: Settings, label: "Configurações" },
];

export default function AdminSidebar() {
  const { profile, signOut } = useAuth();
  const { settings } = useStoreSettings();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = () => {
    // Admin logout → landing page do sistema
    window.location.href = "/";
    signOut();
  };

  const renderNavGroup = (label: string, items: typeof mainNav) => {
    const isExpanded = items.some(
      (i) => i.end ? location.pathname === i.to : location.pathname.startsWith(i.to)
    );

    return (
      <SidebarGroup>
        {!collapsed && <SidebarGroupLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-semibold">{label}</SidebarGroupLabel>}
        <SidebarGroupContent>
          <SidebarMenu>
            {items.map((item) => (
              <SidebarMenuItem key={item.to}>
                <SidebarMenuButton asChild>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-colors"
                    activeClassName="bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary"
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      {/* Header */}
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-3">
          {settings.logo_url ? (
            <img src={settings.logo_url} alt={settings.store_name} className="h-9 w-9 rounded-xl object-contain shrink-0" />
          ) : (
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
              <Store className="h-5 w-5" />
            </div>
          )}
          {!collapsed && (
            <div className="min-w-0">
              <h2 className="font-bold text-sm text-sidebar-foreground truncate">{settings.store_name}</h2>
              <p className="text-[10px] text-muted-foreground">Painel Admin</p>
            </div>
          )}
        </div>
      </SidebarHeader>

      <Separator className="bg-sidebar-border" />

      {/* Navigation */}
      <SidebarContent className="px-2 py-2">
        {renderNavGroup("Principal", mainNav)}
        {renderNavGroup("Catálogo", catalogNav)}
        {renderNavGroup("Financeiro", financialNav)}
        {renderNavGroup("Operações", operationsNav)}
        {renderNavGroup("Sistema", systemNav)}
      </SidebarContent>

      {/* Visit store link */}
      {!collapsed && (
        <div className="px-4 py-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2 text-xs border-sidebar-border text-muted-foreground hover:text-foreground"
            onClick={() => navigate("/")}
          >
            <ExternalLink className="h-3 w-3" />
            Ver loja
          </Button>
        </div>
      )}

      <Separator className="bg-sidebar-border" />

      {/* Footer */}
      <SidebarFooter className="p-3">
        {/* Codxis branding */}
        {!collapsed && (
          <div className="flex items-center gap-2 px-1 mb-2">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-foreground/90">
              <img src={codxisIcon} alt="Codxis" className="h-3.5 w-3.5 object-contain" />
            </div>
            <span className="text-[10px] text-muted-foreground/60">
              Powered by <span className="font-semibold text-muted-foreground/80">Codxis</span>
            </span>
          </div>
        )}

        <Separator className="bg-sidebar-border mb-2" />

        {/* User info */}
        <div className="flex items-center gap-3 px-1">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold">
            {(profile?.full_name || "A").charAt(0).toUpperCase()}
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-sidebar-foreground truncate">{profile?.full_name || "Admin"}</p>
              <p className="text-[10px] text-muted-foreground truncate">{profile?.email}</p>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
            onClick={handleSignOut}
            title="Sair"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
